// ═══ Shared Constants for All Agent Pages ═══

export const PROJECT_TYPES = [
  '학교', '도서관', '병원', '주거', '오피스', '문화시설', '체육시설', '공공청사',
] as const;

export const DESIGN_STYLES = [
  { value: 'modern', label: 'Modern' },
  { value: 'warm', label: 'Warm' },
  { value: 'minimal', label: 'Minimal' },
  { value: 'luxury', label: 'Luxury' },
] as const;

export type ProjectType = typeof PROJECT_TYPES[number];
export type DesignStyle = typeof DESIGN_STYLES[number]['value'];

// ═══ Agent config for icon, color, label ═══
export interface AgentConfig {
  id: string;
  name: string;
  subtitle: string;
  gradient: string;       // from-xxx to-xxx
  accentBg: string;       // bg for active state
  accentText: string;     // text for active state  
  accentBorder: string;   // border for active state
  steps: string[];
}

export const AGENT_CONFIGS: Record<string, AgentConfig> = {
  bf: {
    id: 'bf',
    name: 'BF Agent',
    subtitle: 'AI 장애인편의시설(BF) 인증 자동 검토 · Gemini Flash 연동 · 설계 도면 대비 기준 충족 여부 판정',
    gradient: 'from-sky-500 to-cyan-600',
    accentBg: 'bg-sky-500/20',
    accentText: 'text-sky-300',
    accentBorder: 'border-sky-500/30',
    steps: ['1 정보 입력', '2 AI 분석', '3 기준 대조', '4 결과 출력'],
  },
  color: {
    id: 'color',
    name: 'COLOR Agent',
    subtitle: 'AI 색채 설계 · Gemini Flash 연동 · NCS 분석 · 팔레트 제안 · BF 대비 검증',
    gradient: 'from-pink-500 to-rose-600',
    accentBg: 'bg-pink-500/20',
    accentText: 'text-pink-300',
    accentBorder: 'border-pink-500/30',
    steps: ['1 정보 입력', '2 AI 생성', '3 색채 분석', '4 결과 출력'],
  },
  design: {
    id: 'design',
    name: 'DESIGN Agent',
    subtitle: 'AI 설계 프로그램표 자동 생성 · Gemini Flash 연동 · 실별 면적/마감 검증 · BTL 요구조건 대조',
    gradient: 'from-teal-500 to-emerald-600',
    accentBg: 'bg-teal-500/20',
    accentText: 'text-teal-300',
    accentBorder: 'border-teal-500/30',
    steps: ['1 정보 입력', '2 AI 분석', '3 기준 검증', '4 결과 출력'],
  },
  pay: {
    id: 'pay',
    name: 'PAY Agent',
    subtitle: 'AI 기성관리 v2 · EVM 엔진 · S-Curve 시뮬레이션 · SPI/CPI 분석 · 현금흐름 예측 · 리스크 경보',
    gradient: 'from-violet-500 to-purple-600',
    accentBg: 'bg-violet-500/20',
    accentText: 'text-violet-300',
    accentBorder: 'border-violet-500/30',
    steps: ['1 프로젝트 입력', '2 EVM 엔진', '3 리스크 분석', '4 AI 코멘트'],
  },
  ppt: {
    id: 'ppt',
    name: 'PPT Agent',
    subtitle: 'AI 프레젠테이션 자동 생성 · Gemini Flash 연동 · 슬라이드 구조/콘텐츠 자동 설계',
    gradient: 'from-blue-500 to-indigo-600',
    accentBg: 'bg-blue-500/20',
    accentText: 'text-blue-300',
    accentBorder: 'border-blue-500/30',
    steps: ['1 정보 입력', '2 AI 생성', '3 슬라이드 구성', '4 결과 출력'],
  },
  ren: {
    id: 'ren',
    name: 'REN Agent',
    subtitle: 'AI 렌더링 프롬프트/설정 자동 생성 · Gemini Flash 연동 · SD/ControlNet 프롬프트',
    gradient: 'from-purple-500 to-pink-600',
    accentBg: 'bg-purple-500/20',
    accentText: 'text-purple-300',
    accentBorder: 'border-purple-500/30',
    steps: ['1 설정 입력', '2 AI 생성', '3 프롬프트 최적화', '4 결과 출력'],
  },
  boq: {
    id: 'boq',
    name: 'BOQ Agent',
    subtitle: 'AI 수량 산출 · Gemini Flash 연동 · Excel 파싱 · 물량 검증 · 내역서 자동 생성',
    gradient: 'from-cyan-500 to-blue-600',
    accentBg: 'bg-cyan-500/20',
    accentText: 'text-cyan-300',
    accentBorder: 'border-cyan-500/30',
    steps: ['1 파일 업로드', '2 AI 분석', '3 물량 검증', '4 결과 출력'],
  },
  btl: {
    id: 'btl',
    name: 'BTL Agent',
    subtitle: 'AI BTL 입찰 자동 분석 · Gemini Flash 연동 · 제안서 검토 · 적격심사 예측',
    gradient: 'from-amber-500 to-orange-600',
    accentBg: 'bg-amber-500/20',
    accentText: 'text-amber-300',
    accentBorder: 'border-amber-500/30',
    steps: ['1 문서 입력', '2 AI 분석', '3 적격 심사', '4 결과 출력'],
  },
  cost: {
    id: 'cost',
    name: 'COST Agent',
    subtitle: 'AI 공사비 예측 · Gemini Flash 연동 · VE 분석 · 원가 시뮬레이션 · 리스크 분석',
    gradient: 'from-emerald-500 to-green-600',
    accentBg: 'bg-emerald-500/20',
    accentText: 'text-emerald-300',
    accentBorder: 'border-emerald-500/30',
    steps: ['1 데이터 입력', '2 AI 분석', '3 VE 검토', '4 결과 출력'],
  },
  furniture: {
    id: 'furniture',
    name: 'Furniture Agent',
    subtitle: '제작가구 자동 견적 · 도면 파싱 · 겉넓이 단가 모델 · 카테고리별 가격 분석',
    gradient: 'from-orange-500 to-red-600',
    accentBg: 'bg-orange-500/20',
    accentText: 'text-orange-300',
    accentBorder: 'border-orange-500/30',
    steps: ['1 가구 입력', '2 자동 분류', '3 단가 산출', '4 견적서 출력'],
  },
};

// ═══ Export helper for AI stream parsing ═══
export function downloadAsTextFile(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
